import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderListElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.CUSTOM_LIST:
  const iconOptions = ['check', 'red-x', 'pink-check', 'dark-check', 'green-badge', 'star-badge', 'star', 'info', 'warning'];

  // Debug padding
  console.log('🔍 CUSTOM_LIST rendering - element.styles:', element.styles);
  console.log('🔍 CUSTOM_LIST padding value:', element.styles?.padding);

  return (
    <Box
      sx={{ ...element.styles, mb: 0.5, position: 'relative', '&:hover .element-controls': { opacity: 1 }, ...getSelectionStyles(element.id) }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Custom List clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
        // Close icon selector when clicking elsewhere
        setIconSelectorOpen(null);
      }}
      title="Click to select and edit styles • Double-click to edit text"
    >
      <ElementControls />
      {element.rows.map((row, idx) => (
        isEditingField(`rows.${idx}.text`) ? (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {getListIcon(row.icon, idx)}
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveInlineEdit}
              autoFocus
              variant="outlined"
              size="small"
              sx={{
                ml: 1,
                flex: 1,
                '& .MuiInputBase-input': {
                  color: (() => {
                    const presetColor = getPreset(element.textStylePreset || 'bullet1')?.color;
                    const elementColor = element.styles?.color;
                    const finalColor = presetColor || elementColor || '#000000';
                    console.log('🎨 Custom list text color debug:', {
                      textStylePreset: element.textStylePreset,
                      presetColor,
                      elementColor,
                      finalColor
                    });
                    return `${finalColor} !important`;
                  })()
                }
              }}
            />
          </Box>
        ) : (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, fontFamily: element.styles?.fontFamily, fontSize: element.styles?.fontSize, color: element.styles?.color }}>
            <Box
              sx={{
                mr: 1,
                cursor: 'pointer',
                position: 'relative',
                '&:hover': { opacity: 0.7 }
              }}
              onClick={(e) => {
                e.stopPropagation();
                const selectorKey = `${element.id}-${idx}`;
                setIconSelectorOpen(iconSelectorOpen === selectorKey ? null : selectorKey);
              }}
              title="Click to change icon"
            >
              {getListIcon(row.icon, idx)}

              {/* Icon Selector Popup */}
              {iconSelectorOpen === `${element.id}-${idx}` && (
                <Box
                  className="icon-selector-popup"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    p: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                    minWidth: '150px'
                  }}
                >
                  {iconOptions.map((iconType) => (
                    <Box
                      key={iconType}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        border: row.icon === iconType ? '2px solid #1976d2' : '1px solid transparent'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Update the icon for this row
                        const updatedRows = [...element.rows];
                        updatedRows[idx] = { ...updatedRows[idx], icon: iconType };
                        updateElement(element.id, { rows: updatedRows }, isInZone, layoutId, zoneId);
                        setIconSelectorOpen(null);
                      }}
                      title={iconType}
                    >
                      {getListIcon(iconType, 0)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.3)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                },
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onDoubleClick={() => handleDoubleClick(`rows.${idx}.text`, row.text)}
              title="Double-click to edit text"
            >
              <span style={{
                ...getPreset(element.textStylePreset || 'paragraph1'),
                fontFamily: getPreset(element.textStylePreset || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                fontSize: getPreset(element.textStylePreset || 'paragraph1')?.fontSize || element.styles?.fontSize || 'inherit',
                color: (() => {
                  const presetColor = getPreset(element.textStylePreset || 'paragraph1')?.color;
                  const elementColor = element.styles?.color;
                  const finalColor = elementColor || presetColor || 'inherit';
                  console.log('🎨 Custom list display text color:', {
                    textStylePreset: element.textStylePreset,
                    presetColor,
                    elementColor,
                    finalColor,
                    elementStyles: element.styles
                  });
                  return finalColor;
                })(),
                fontWeight: getPreset(element.textStylePreset || 'paragraph1')?.fontWeight || 'inherit',
                lineHeight: getPreset(element.textStylePreset || 'paragraph1')?.lineHeight || 'inherit',
                letterSpacing: getPreset(element.textStylePreset || 'paragraph1')?.letterSpacing || 'inherit',
                textAlign: getPreset(element.textStylePreset || 'paragraph1')?.textAlign || 'inherit'
              }}>
                {row.text}
              </span>

            </Box>
          </Box>
        )
      ))}
    </Box>
  );

case ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE:
  const iconOptions2 = ['check', 'red-x', 'pink-check', 'dark-check', 'green-badge', 'star-badge', 'star', 'info', 'warning'];

  return (
    <Box
      sx={{ ...element.styles, mb: 2, position: 'relative', '&:hover .element-controls': { opacity: 1 }, ...getSelectionStyles(element.id) }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Two-Line Custom List clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
        // Close icon selector when clicking elsewhere
        setIconSelectorOpen(null);
      }}
      title="Click to select and edit styles • Double-click to edit text"
    >
      <ElementControls />
      {element.rows.map((row, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              mr: 1,
              cursor: 'pointer',
              position: 'relative',
              '&:hover': { opacity: 0.7 },
              mt: 0.5 // Slight top margin to align with first line
            }}
            onClick={(e) => {
              e.stopPropagation();
              const selectorKey = `${element.id}-${idx}`;
              setIconSelectorOpen(iconSelectorOpen === selectorKey ? null : selectorKey);
            }}
            title="Click to change icon"
          >
            {getListIcon(row.icon, idx)}

            {/* Icon Selector Popup */}
            {iconSelectorOpen === `${element.id}-${idx}` && (
              <Box
                className="icon-selector-popup"
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  p: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  minWidth: '150px'
                }}
              >
                {iconOptions2.map((iconType) => (
                  <Box
                    key={iconType}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      border: row.icon === iconType ? '2px solid #1976d2' : '1px solid transparent'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Update the icon for this row
                      const updatedRows = [...element.rows];
                      updatedRows[idx] = { ...updatedRows[idx], icon: iconType };
                      updateElement(element.id, { rows: updatedRows }, isInZone, layoutId, zoneId);
                      setIconSelectorOpen(null);
                    }}
                    title={iconType}
                  >
                    {getListIcon(iconType, 0)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Two-line text content */}
          <Box sx={{ flex: 1 }}>
            {/* First line (title) */}
            {isEditingField(`rows.${idx}.text1`) ? (
              <TextField
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={saveInlineEdit}
                autoFocus
                variant="outlined"
                size="small"
                sx={{
                  mb: 0.5,
                  width: '100%',
                  '& .MuiInputBase-input': {
                    color: element.styles?.color || getPreset(element.textStylePreset1 || 'subheading1')?.color || 'inherit'
                  }
                }}
                placeholder="Title"
              />
            ) : (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  },
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  mb: 0.5
                }}
                onDoubleClick={() => handleDoubleClick(`rows.${idx}.text1`, row.text1)}
                title="Double-click to edit title"
              >
                <span style={{
                  ...getPreset(element.textStylePreset1 || 'subheading1'),
                  fontFamily: getPreset(element.textStylePreset1 || 'subheading1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                  fontSize: getPreset(element.textStylePreset1 || 'subheading1')?.fontSize || element.styles?.fontSize || 'inherit',
                  color: element.styles?.color || getPreset(element.textStylePreset1 || 'subheading1')?.color || 'inherit',
                  fontWeight: getPreset(element.textStylePreset1 || 'subheading1')?.fontWeight || 'inherit',
                  lineHeight: getPreset(element.textStylePreset1 || 'subheading1')?.lineHeight || 'inherit',
                  letterSpacing: getPreset(element.textStylePreset1 || 'subheading1')?.letterSpacing || 'inherit',
                  textAlign: getPreset(element.textStylePreset1 || 'subheading1')?.textAlign || 'inherit'
                }}>
                  {row.text1}
                </span>
              </Box>
            )}

            {/* Second line (description) */}
            {isEditingField(`rows.${idx}.text2`) ? (
              <TextField
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={saveInlineEdit}
                autoFocus
                variant="outlined"
                size="small"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    color: element.styles?.color || getPreset(element.textStylePreset2 || 'paragraph1')?.color || 'inherit'
                  }
                }}
                placeholder="Description"
              />
            ) : (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  },
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onDoubleClick={() => handleDoubleClick(`rows.${idx}.text2`, row.text2)}
                title="Double-click to edit description"
              >
                <span style={{
                  ...getPreset(element.textStylePreset2 || 'paragraph1'),
                  fontFamily: getPreset(element.textStylePreset2 || 'paragraph1')?.fontFamily || element.styles?.fontFamily || 'inherit',
                  fontSize: getPreset(element.textStylePreset2 || 'paragraph1')?.fontSize || element.styles?.fontSize || 'inherit',
                  color: element.styles?.color || getPreset(element.textStylePreset2 || 'paragraph1')?.color || 'inherit',
                  fontWeight: getPreset(element.textStylePreset2 || 'paragraph1')?.fontWeight || 'inherit',
                  lineHeight: getPreset(element.textStylePreset2 || 'paragraph1')?.lineHeight || 'inherit',
                  letterSpacing: getPreset(element.textStylePreset2 || 'paragraph1')?.letterSpacing || 'inherit',
                  textAlign: getPreset(element.textStylePreset2 || 'paragraph1')?.textAlign || 'inherit'
                }}>
                  {row.text2}
                </span>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

case ELEMENT_TYPES.TIMELINE_LIST:
  return (
    <Box
      sx={{
        ...element.styles,
        mb: 2,
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Timeline List clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
      }}
      title="Click to select and edit styles • Double-click to edit steps"
    >
      <ElementControls />
      {element.steps.map((step, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', flexDirection: 'column', position: 'relative' }}>
            <Box
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: step.dotColor,
                mb: 1,
                zIndex: 2,
                position: 'relative'
              }}
            />
            {idx < element.steps.length - 1 && (
              <Box
                sx={{
                  width: '2px',
                  height: '60px',
                  backgroundColor: '#e0e0e0',
                  position: 'absolute',
                  top: '32px',
                  left: '11px',
                  zIndex: 1
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            {/* Stage Label */}
            {isEditingField(`steps.${idx}.stageLabel`) ? (
              <TextField
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={saveInlineEdit}
                autoFocus
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1 }}
              />
            ) : (
              <Box
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  },
                  transition: 'all 0.2s ease',
                }}
                onDoubleClick={() => handleDoubleClick(`steps.${idx}.stageLabel`, step.stageLabel)}
                title="Double-click to edit stage label"
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: step.stageLabelColor,
                    fontWeight: 'bold'
                  }}
                >
                  {step.stageLabel}
                </Typography>
              </Box>
            )}

            {/* Title */}
            {isEditingField(`steps.${idx}.title`) ? (
              <TextField
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={saveInlineEdit}
                autoFocus
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mb: 1 }}
              />
            ) : (
              <Box
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  },
                  transition: 'all 0.2s ease',
                }}
                onDoubleClick={() => handleDoubleClick(`steps.${idx}.title`, step.title)}
                title="Double-click to edit title"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  {step.title}
                </Typography>
              </Box>
            )}

            {/* Description */}
            {isEditingField(`steps.${idx}.description`) ? (
              <TextField
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={saveInlineEdit}
                autoFocus
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            ) : (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(25, 118, 210, 0.3)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  },
                  transition: 'all 0.2s ease',
                }}
                onDoubleClick={() => handleDoubleClick(`steps.${idx}.description`, step.description)}
                title="Double-click to edit description"
              >
                <Typography
                  variant="body1"
                >
                  {step.description}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

case ELEMENT_TYPES.PROCESS_STEPS:
  return (
    <Box
      sx={{
        ...element.styles,
        mb: 2,
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        console.log('🔴 Process Steps clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(1); // Set to Style tab
        setRightPanelOpen(true); // Ensure right panel is open
      }}
      title="Click to select and edit styles • Double-click to edit steps"
    >
      <ElementControls />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {element.steps.map((step, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            {/* Image with Numbered Badge */}
            <Box sx={{ flexShrink: 0, width: '150px', position: 'relative' }}>
              {isEditingField(`steps.${idx}.image`) ? (
                <TextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onBlur={saveInlineEdit}
                  autoFocus
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Enter image URL"
                />
              ) : (
                <Box
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      '& .image-overlay': {
                        opacity: 1
                      }
                    }
                  }}
                  onDoubleClick={() => handleDoubleClick(`steps.${idx}.image`, step.image)}
                  title="Double-click to edit image URL"
                >
                  <img
                    src={step.image}
                    alt={`Step ${step.number}`}
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0'
                    }}
                    onError={(e) => {
                      if (e.target.src !== `https://via.placeholder.com/150x120?text=Step+${step.number}`) {
                        e.target.src = `https://via.placeholder.com/150x120?text=Step+${step.number}`;
                      }
                    }}
                  />

                  {/* Numbered Badge - Top Right Corner */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: step.numberColor || '#1976d2',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1
                    }}
                  >
                    {step.number}
                  </Box>

                  <Box
                    className="image-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '12px',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      fontSize: '20px'
                    }}
                  >
                  </Box>
                </Box>
              )}
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              {/* Title */}
              {isEditingField(`steps.${idx}.title`) ? (
                <TextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onBlur={saveInlineEdit}
                  autoFocus
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              ) : (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid transparent',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      border: '1px solid rgba(25, 118, 210, 0.3)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onDoubleClick={() => handleDoubleClick(`steps.${idx}.title`, step.title)}
                  title="Double-click to edit title"
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                    {step.title}
                  </Typography>
                </Box>
              )}

              {/* Description */}
              {isEditingField(`steps.${idx}.description`) ? (
                <TextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onBlur={saveInlineEdit}
                  autoFocus
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                />
              ) : (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid transparent',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      border: '1px solid rgba(25, 118, 210, 0.3)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onDoubleClick={() => handleDoubleClick(`steps.${idx}.description`, step.description)}
                  title="Double-click to edit description"
                >
                  <Typography variant="body1" sx={{ color: 'inherit' }}>
                    {step.description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
    default:
      return null;
  }
};
